import React, { useState, useCallback } from 'react';
import { Button, message, Drawer } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useForm } from '@refinedev/antd';
import { CommonForm } from './CommonForm';

interface CreateFormProps {
  resource: string;
  fields: any[];
  title?: string;
  submitText?: string;
  initialValues?: Record<string, any>;
  open?: boolean;
  onClose?: () => void;
  onSuccess?: (data: any) => void;
  drawerWidth?: number;
  modalWidth?: number;
  formDisplayMode?: 'drawer' | 'modal' | 'inline';
  // Chat configuration
  enableChat?: boolean;
  chatType?: string;
  chatRelativeId?: number;
  chatTitle?: string;
  chatPlaceholder?: string;
}

export const CreateForm: React.FC<CreateFormProps> = ({
  resource,
  fields,
  title = 'Create',
  submitText = 'Create',
  initialValues = {},
  open = false,
  onClose,
  onSuccess,
  drawerWidth,
  modalWidth,
  formDisplayMode = 'drawer',
  enableChat,
  chatType,
  chatRelativeId,
  chatTitle,
  chatPlaceholder,
}) => {
  const [isVisible, setIsVisible] = useState(open);
  const hasSetInitialValues = React.useRef(false);

  // Calculate initial values when form opens
  const getInitialValues = useCallback(() => {
    if (!fields) return {};

    const calculatedInitialValues: Record<string, any> = {};

    fields.forEach(field => {
      if (field.defaultValue !== undefined) {
        // Handle function-based default values
        if (typeof field.defaultValue === 'function') {
          calculatedInitialValues[field.name] = field.defaultValue();
        } else {
          calculatedInitialValues[field.name] = field.defaultValue;
        }
      }
    });

    return calculatedInitialValues;
  }, [fields]);

  // Always call useForm to avoid conditional hook issues
  const {
    form,
    formProps: createFormProps,
    saveButtonProps: createSaveButtonProps,
  } = useForm({
    resource,
    action: 'create',
    redirect: false,
    onMutationSuccess: (data: any) => {
      message.success('Record created successfully!');
      setIsVisible(false);
      form?.resetFields();
      onSuccess?.(data);
    },
    onMutationError: (error: any) => {
      message.error(
        error?.response?.data?.message || 'Failed to create record. Please check your input and try again.'
      );
    },
  });

  const handleClose = () => {
    setIsVisible(false);
    form?.resetFields();
    onClose?.();
  };

  // Update visibility when prop changes
  React.useEffect(() => {
    setIsVisible(open);
  }, [open]);

  // Set initial values when form becomes visible
  React.useEffect(() => {
    if (isVisible && form && !hasSetInitialValues.current) {
      hasSetInitialValues.current = true;
      setTimeout(() => {
        const valuesToSet = { ...getInitialValues(), ...initialValues };
        form.setFieldsValue(valuesToSet);
      }, 100);
    }
    
    // Reset the flag when form closes
    if (!isVisible) {
      hasSetInitialValues.current = false;
    }
  }, [isVisible]); // Only depend on isVisible to prevent multiple triggers

  if (!open) return null;

  return (
    <CommonForm
      fields={fields}
      title={title}
      submitText={submitText}
      initialValues={{ ...getInitialValues(), ...initialValues }}
      formDisplayMode={formDisplayMode}
      open={isVisible}
      drawerWidth={drawerWidth}
      modalWidth={modalWidth}
      form={form}
      formProps={createFormProps}
      saveButtonProps={createSaveButtonProps}
      onClose={handleClose}
      onFinish={createFormProps.onFinish}
      resourceName={resource}
      enableChat={enableChat}
      chatType={chatType}
      chatRelativeId={chatRelativeId}
      chatTitle={chatTitle}
      chatPlaceholder={chatPlaceholder}
    />
  );
};
